import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { RedactionText } from '../../redaction/RedactionText';
import { pageContextApi, type PageEvent, type PageEventMapResponse, type PageThemesResponse } from '../../../services/pageContextApi';
import { FileText, ChevronDown, ChevronRight, Loader2, MapPin } from 'lucide-react';

interface PageOutlineProps {
  documentId: string;
  currentPage: number;
  totalPages: number;
  onNavigateToPage: (page: number) => void;
}

function formatEventType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

const SIGNIFICANCE_COLOURS: Record<string, string> = {
  MAJOR: 'bg-primary-500',
  SUPPORTING: 'bg-amber-500',
  ROUTINE: 'bg-medical-300 dark:bg-medical-600',
};

const EVENT_TYPE_COLOURS: Record<string, string> = {
  ADMISSION: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
  DISCHARGE: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  DIAGNOSIS: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  MAJOR_PROCEDURE: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  MEDICATION: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  LAB_RESULT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  INVESTIGATION: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

const DEFAULT_TYPE_COLOUR = 'bg-medical-100 text-medical-600 dark:bg-medical-700 dark:text-medical-300';

/**
 * Page Outline — a table of contents for the current document.
 * Shows each page with its related chronology events.
 * The current page auto-expands and scrolls into view as you navigate.
 */
export const PageOutline: React.FC<PageOutlineProps> = ({
  documentId,
  currentPage,
  totalPages,
  onNavigateToPage,
}) => {
  // Fetch the event map (page -> event count) for the whole document
  const { data: eventMapData, isLoading: loadingMap } = useQuery<PageEventMapResponse>(
    ['pageEventMap', documentId],
    () => pageContextApi.getPageEventMap(documentId),
    { enabled: !!documentId, staleTime: 5 * 60 * 1000, retry: 1 },
  );

  // Fetch themes per page
  const { data: themesData } = useQuery<PageThemesResponse>(
    ['pageThemes', documentId],
    () => pageContextApi.getPageThemes(documentId),
    { enabled: !!documentId, staleTime: 5 * 60 * 1000, retry: 1 },
  );

  // Track which page is expanded (auto-expand current page)
  const [expandedPage, setExpandedPage] = useState<number | null>(currentPage);

  // Auto-expand current page when it changes
  useEffect(() => {
    setExpandedPage(currentPage);
  }, [currentPage]);

  // Scroll the current page entry into view
  const activeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentPage]);

  // Build page list
  const pages = useMemo(() => {
    const result: Array<{
      pageNumber: number;
      eventCount: number;
      themes: string[];
    }> = [];

    for (let p = 1; p <= totalPages; p++) {
      result.push({
        pageNumber: p,
        eventCount: eventMapData?.pageEventCounts?.[p] ?? 0,
        themes: themesData?.pageThemes?.[p] ?? [],
      });
    }
    return result;
  }, [totalPages, eventMapData, themesData]);

  if (loadingMap && totalPages === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 text-primary-500 animate-spin" />
        <span className="ml-2 text-[10px] text-medical-500">Loading outline...</span>
      </div>
    );
  }

  if (totalPages === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <FileText className="h-6 w-6 text-medical-300 dark:text-medical-600 mb-2" />
        <p className="text-[10px] text-medical-400">No pages available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="px-3 py-2 border-b border-medical-100 dark:border-medical-700 bg-medical-50/50 dark:bg-medical-900/30">
        <span className="text-[10px] font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wide">
          Page outline
        </span>
        <span className="ml-2 text-[10px] text-medical-400">
          {totalPages} page{totalPages !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Scrollable page list */}
      <div className="flex-1 overflow-y-auto">
        {pages.map((page) => {
          const isCurrent = page.pageNumber === currentPage;
          const isExpanded = page.pageNumber === expandedPage;
          const hasEvents = page.eventCount > 0;

          return (
            <div
              key={page.pageNumber}
              ref={isCurrent ? activeRef : undefined}
              className={`border-b border-medical-100/50 dark:border-medical-700/50 ${
                isCurrent ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''
              }`}
            >
              {/* Page row */}
              <button
                onClick={() => {
                  if (page.pageNumber !== currentPage) {
                    onNavigateToPage(page.pageNumber);
                  }
                  setExpandedPage(isExpanded ? null : page.pageNumber);
                }}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 transition-colors group ${
                  isCurrent
                    ? 'hover:bg-primary-100/50 dark:hover:bg-primary-900/30'
                    : 'hover:bg-medical-50 dark:hover:bg-medical-700/30'
                }`}
              >
                {/* Expand/collapse chevron */}
                {hasEvents ? (
                  isExpanded ? (
                    <ChevronDown className="h-3 w-3 text-medical-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-3 w-3 text-medical-400 flex-shrink-0" />
                  )
                ) : (
                  <span className="w-3 flex-shrink-0" />
                )}

                {/* Page number */}
                <span className={`text-[11px] tabular-nums font-medium min-w-[28px] ${
                  isCurrent
                    ? 'text-primary-700 dark:text-primary-300'
                    : 'text-medical-600 dark:text-medical-400'
                }`}>
                  p.{page.pageNumber}
                </span>

                {/* Current page indicator */}
                {isCurrent && (
                  <MapPin className="h-3 w-3 text-primary-500 flex-shrink-0" />
                )}

                {/* Theme pills (compact, max 2) — or muted label for pages with no chronology */}
                <div className="flex-1 min-w-0 flex items-center gap-1 overflow-hidden">
                  {page.themes.length > 0 ? (
                    <>
                      {page.themes.slice(0, 2).map((theme, i) => (
                        <span
                          key={i}
                          className={`px-1.5 py-0.5 text-[8px] font-medium rounded-full truncate ${
                            EVENT_TYPE_COLOURS[theme] || DEFAULT_TYPE_COLOUR
                          }`}
                        >
                          {formatEventType(theme)}
                        </span>
                      ))}
                      {page.themes.length > 2 && (
                        <span className="text-[8px] text-medical-400">
                          +{page.themes.length - 2}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-[10px] italic text-medical-300 dark:text-medical-600">
                      No chronology
                    </span>
                  )}
                </div>

                {/* Event count badge */}
                {hasEvents && (
                  <span className="shrink-0 px-1 py-0.5 text-[8px] font-semibold rounded bg-medical-100 text-medical-500 dark:bg-medical-700 dark:text-medical-400 tabular-nums">
                    {page.eventCount}
                  </span>
                )}
              </button>

              {/* Expanded: show events for this page */}
              {isExpanded && hasEvents && (
                <PageEventsList
                  documentId={documentId}
                  pageNumber={page.pageNumber}
                  onNavigateToPage={onNavigateToPage}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Lazy-loaded events list for an expanded page in the outline.
 * Fetches the full page context only when the page is expanded.
 */
const PageEventsList: React.FC<{
  documentId: string;
  pageNumber: number;
  onNavigateToPage: (page: number) => void;
}> = ({ documentId, pageNumber, onNavigateToPage }) => {
  const { data, isLoading } = useQuery(
    ['pageContext', documentId, pageNumber],
    () => pageContextApi.getPageContext(documentId, pageNumber),
    { staleTime: 5 * 60 * 1000, retry: 1 },
  );

  if (isLoading) {
    return (
      <div className="px-8 py-2">
        <Loader2 className="h-3 w-3 text-medical-400 animate-spin" />
      </div>
    );
  }

  if (!data?.events || data.events.length === 0) {
    return (
      <div className="px-8 py-2">
        <p className="text-[9px] text-medical-400 italic">No event details available</p>
      </div>
    );
  }

  return (
    <div className="pl-8 pr-3 pb-2 space-y-1.5">
      {data.events.map((event: PageEvent) => (
        <button
          key={event.eventId}
          onClick={() => onNavigateToPage(pageNumber)}
          className="w-full text-left flex items-start gap-2 py-1 rounded hover:bg-medical-50 dark:hover:bg-medical-700/30 transition-colors cursor-pointer"
        >
          {/* Significance dot */}
          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
            SIGNIFICANCE_COLOURS[event.significance] || SIGNIFICANCE_COLOURS.ROUTINE
          }`} />

          <div className="min-w-0 flex-1">
            {/* Headline */}
            <p className="text-[10px] font-medium text-medical-700 dark:text-medical-200 leading-tight line-clamp-2">
              <RedactionText text={event.headline} />
            </p>

            {/* Date + type */}
            <div className="flex items-center gap-1.5 mt-0.5">
              {event.eventDate && (
                <span className="text-[8px] text-medical-400 tabular-nums">
                  {formatDate(event.eventDate)}
                </span>
              )}
              <span className={`px-1 py-0.5 text-[7px] font-medium rounded ${
                EVENT_TYPE_COLOURS[event.eventType] || DEFAULT_TYPE_COLOUR
              }`}>
                {formatEventType(event.eventType)}
              </span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};
